import cn from 'classnames';
import React, { useCallback, useState } from 'react';
import type { ChangeEvent } from 'react';

import Icon, { IconTypes } from 'components/basic/Icon';

import s from './InputText.module.scss';

interface Props<ObjectShape> {
  labelText?: string | JSX.Element;
  value?: string | number;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  name: keyof ObjectShape;
  className?: string;
  errorMessage?: string;
  type?: 'text' | 'number' | 'textarea' | 'password';
  doValidate?: (field: keyof ObjectShape, value: string) => Promise<void>;
  autocomplete?: string;
  placeholder?: string;
  helpText?: string;
  maxLength?: number;
  isDisabled?: boolean;
}

export default function InputText<ObjectShape>(
  props: Props<ObjectShape>,
): JSX.Element {
  const {
    labelText,
    value,
    onChange: onChangeProp,
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    name,
    errorMessage,
    type = 'text',
    doValidate,
    autocomplete,
    placeholder,
    maxLength = 0,
    isDisabled,
    className,
  } = props;
  const [isPasswordShowed, setPasswordShowed] = useState(false);

  async function onChange(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): Promise<void> {
    if (onChangeProp) {
      onChangeProp(e.target.value);
    }

    if (doValidate && e.target.value !== '') {
      await doValidate(name, e.target.value);
    }
  }

  const onBlur = useCallback(async () => {
    if (doValidate && typeof value === 'string') {
      await doValidate(name, value);
    }

    onBlurProp?.();
  }, [value, name, doValidate]);

  const onFocus = () => {
    if (onFocusProp) {
      onFocusProp();
    }
  };

  const hasErrorMessage = errorMessage !== undefined && errorMessage.length > 0;
  const hasPlaceholder = placeholder !== undefined && placeholder.length > 0;

  let input = <div />;

  let valueLength = 0;

  if (value) {
    valueLength = value.toString().length;
  }

  if (type === 'text') {
    input = (
      <input
        id={name as string}
        value={value}
        disabled={isDisabled}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        type={type}
        autoComplete={autocomplete}
        placeholder={placeholder}
        className={cn(s.input, {
          [s.withErrorBorder]: hasErrorMessage,
        })}
      />
    );
  }
  else if (type === 'number') {
    input = (
      <input
        id={name as string}
        value={value}
        disabled={isDisabled}
        onChange={onChange}
        onBlur={onBlur}
        type={type}
        autoComplete={autocomplete}
        placeholder={placeholder}
        className={cn(s.input, {
          [s.withErrorBorder]: hasErrorMessage,
        })}
      />
    );
  }
  else if (type === 'password') {
    input = (
      <input
        id={name as string}
        value={value}
        disabled={isDisabled}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        type={isPasswordShowed ? 'text' : 'password'}
        autoComplete={autocomplete}
        className={cn(s.input, s.withIcon, {
          [s.withErrorBorder]: hasErrorMessage,
        })}
      />
    );
  }
  else if (type === 'textarea') {
    input = (
      <textarea
        id={name as string}
        value={value}
        disabled={isDisabled}
        onChange={onChange}
        placeholder={placeholder}
        onBlur={onBlur}
        onFocus={onFocus}
        autoComplete={autocomplete}
        className={cn(s.input, s.textAriaSize, {
          [s.withErrorBorder]: hasErrorMessage,
        })}
      />
    );
  }

  return (
    <div className={className}>
      <label
        htmlFor={name as string}
        className={cn(s.label, {
          [s.labelWithError]: errorMessage,
        })}
      >
        {labelText && (
          <>
            {input}
            <div
              className={cn(s.floatLabel, {
                [s.backLabelColor]: type === 'textarea',
                [s.withError]: hasErrorMessage,
                [s.activeLabel]: hasPlaceholder,
                [s.hasValue]: !!value,
              })}
            >
              {labelText}
            </div>
          </>
        )}

        {type === 'password' && (
          <div
            className={s.passwordEye}
            onClick={(): void => setPasswordShowed(current => !current)}
          >
            <Icon
              icon={isPasswordShowed ? IconTypes.eye : IconTypes.eyeClosed}
              size={24}
              color="#1a87f5"
            />
          </div>
        )}
      </label>
      {(hasErrorMessage) && (
        <div className={cn(s.wrapperInfo)}>
          {hasErrorMessage && (
            <div
              className={cn(s.helpText, {
                [s.withError]: hasErrorMessage,
              })}
            >
              {errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
